import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Col,
  Row,
  Typography,
  Form,
  Input,
  Select,
  Button,
  Table,
  Tag,
  Space,
  message,
  Tooltip,
  Empty,
  Modal
} from 'antd';
import {
  PlusCircleOutlined,
  ReloadOutlined,
  MessageOutlined,
  EditOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

type PanelTodoCategory = 'bug' | 'feature' | 'improvement' | 'other';
type PanelTodoStatus = 'pending' | 'in_progress' | 'developing' | 'fixing' | 'resolved' | 'closed';

interface PanelTodoFormValues {
  title: string;
  category: PanelTodoCategory;
  description?: string;
  contactEmail?: string;
}

interface PanelTodoItem {
  id: number;
  user_id: number;
  title: string;
  description: string | null;
  category: PanelTodoCategory;
  status: PanelTodoStatus;
  admin_reply: string | null;
  admin_id: number | null;
  reply_at: string | null;
  created_at: string;
  updated_at: string;
  submitter_username?: string;
  submitter_email?: string;
  admin_username?: string;
  key?: React.Key;
}

const CATEGORY_OPTIONS: { value: PanelTodoCategory; label: string }[] = [
  { value: 'bug', label: 'BUG / 问题反馈' },
  { value: 'feature', label: '功能新增需求' },
  { value: 'improvement', label: '功能优化建议' },
  { value: 'other', label: '其他' }
];

const CATEGORY_LABELS: Record<PanelTodoCategory, string> = {
  bug: 'BUG / 问题反馈',
  feature: '功能新增需求',
  improvement: '功能优化建议',
  other: '其他'
};

const STATUS_LABELS: Record<PanelTodoStatus, string> = {
  pending: '待处理',
  in_progress: '处理中',
  developing: '开发中',
  fixing: '修复中',
  resolved: '已修复',
  closed: '已关闭'
};

const STATUS_COLORS: Record<PanelTodoStatus, string> = {
  pending: 'default',
  in_progress: 'processing',
  developing: 'processing',
  fixing: 'warning',
  resolved: 'success',
  closed: 'default'
};

const PanelTodo: React.FC = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [adminForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [adminUpdating, setAdminUpdating] = useState(false);
  const [todos, setTodos] = useState<PanelTodoItem[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentTodo, setCurrentTodo] = useState<PanelTodoItem | null>(null);

  const fetchTodos = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<PanelTodoItem[]>('/panel-todos');
      setTodos(data || []);
    } catch (error) {
      console.error('获取TODO列表失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  useEffect(() => {
    if (user?.email) {
      const currentEmail = form.getFieldValue('contactEmail');
      if (!currentEmail) {
        form.setFieldsValue({ contactEmail: user.email });
      }
    }
  }, [user?.email, form]);

  const handleSubmit = async (values: PanelTodoFormValues) => {
    setSubmitLoading(true);
    try {
      await api.post('/panel-todos', values);
      message.success('已提交反馈');
      form.resetFields();
      form.setFieldsValue({
        category: 'bug',
        contactEmail: user?.email
      });
      fetchTodos();
    } catch (error: any) {
      console.error('提交TODO失败:', error);
      const errorMessage = error?.response?.data?.message || '提交失败，请稍后再试';
      message.error(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  const openAdminModal = (record: PanelTodoItem) => {
    setCurrentTodo(record);
    adminForm.setFieldsValue({
      status: record.status,
      adminReply: record.admin_reply || ''
    });
    setModalVisible(true);
  };

  const handleAdminUpdate = async () => {
    try {
      const values = await adminForm.validateFields();
      if (!currentTodo) return;
      setAdminUpdating(true);
      await api.put(`/panel-todos/${currentTodo.id}`, values);
      message.success('记录已更新');
      setModalVisible(false);
      fetchTodos();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('更新TODO失败:', error);
      const errorMessage = error?.response?.data?.message || '更新失败，请稍后再试';
      message.error(errorMessage);
    } finally {
      setAdminUpdating(false);
    }
  };

  const columns = useMemo(() => {
    return [
      {
        title: '标题',
        dataIndex: 'title',
        key: 'title',
        render: (_: string, record: PanelTodoItem) => (
          <Space direction="vertical" size={0}>
            <Text strong>{record.title}</Text>
            <Tag color="blue">{CATEGORY_LABELS[record.category]}</Tag>
          </Space>
        )
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        render: (status: PanelTodoStatus) => (
          <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
        )
      },
      {
        title: '提交人',
        dataIndex: 'submitter_username',
        key: 'submitter_username',
        render: (_: string, record: PanelTodoItem) => (
          <Space direction="vertical" size={0}>
            <Text>{record.submitter_username || '未知用户'}</Text>
            <Text type="secondary">{dayjs(record.created_at).format('YYYY-MM-DD HH:mm')}</Text>
          </Space>
        )
      },
      {
        title: '管理员回复',
        dataIndex: 'admin_reply',
        key: 'admin_reply',
        render: (reply: string | null, record: PanelTodoItem) =>
          reply ? (
            <Space direction="vertical" size={0}>
              <Paragraph style={{ marginBottom: 0 }}>{reply}</Paragraph>
              <Text type="secondary">
                {record.admin_username ? `回复人：${record.admin_username}` : '管理员'}
                {record.reply_at ? ` · ${dayjs(record.reply_at).format('YYYY-MM-DD HH:mm')}` : ''}
              </Text>
            </Space>
          ) : (
            <Text type="secondary">尚无回复</Text>
          )
      },
      ...(user?.role === 'admin'
        ? [
            {
              title: '操作',
              key: 'action',
              render: (_: unknown, record: PanelTodoItem) => (
                <Space>
                  <Button icon={<EditOutlined />} onClick={() => openAdminModal(record)}>
                    更新
                  </Button>
                </Space>
              )
            }
          ]
        : [])
    ];
  }, [user?.role]);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Title level={3} style={{ marginBottom: 0 }}>
                面板 TODO / 需求反馈
              </Title>
              <Text type="secondary">
                在这里提交面板相关的BUG、功能需求或优化建议。用户可以查看进度，管理员会及时更新处理状态。
              </Text>
            </Space>
          </Card>
        </Col>

        <Col span={24}>
          <Card title="提交新的反馈">
            <Form
              layout="vertical"
              form={form}
              onFinish={handleSubmit}
              initialValues={{
                category: 'bug' as PanelTodoCategory,
                contactEmail: user?.email
              }}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="title"
                    label="标题"
                    rules={[
                      { required: true, message: '请输入标题' },
                      { max: 200, message: '标题长度不能超过200个字符' }
                    ]}
                  >
                    <Input placeholder="简要描述问题或需求，带上游戏名" allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="category"
                    label="分类"
                    rules={[{ required: true, message: '请选择分类' }]}
                  >
                    <Select options={CATEGORY_OPTIONS} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="contactEmail"
                    label="进度反馈邮箱（可选）"
                    rules={[{ type: 'email', message: '请输入有效的邮箱地址' }]}
                  >
                    <Input placeholder="用于接收处理进度的邮箱" allowClear />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="description"
                label="详细描述"
                rules={[{ max: 5000, message: '详情长度不能超过5000个字符' }]}
              >
                <TextArea
                  placeholder="请提供尽量详细的信息，例如：如何复现、预期结果、实际结果等。"
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  allowClear
                />
              </Form.Item>

              <Space>
                <Button
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  htmlType="submit"
                  loading={submitLoading}
                >
                  提交
                </Button>
                <Tooltip title="刷新列表">
                  <Button icon={<ReloadOutlined />} onClick={fetchTodos} />
                </Tooltip>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title="反馈列表"
            extra={
              <Space>
                <MessageOutlined />
                <Text type="secondary">已提交 {todos.length} 条记录</Text>
              </Space>
            }
          >
            <Table<PanelTodoItem>
              dataSource={todos.map((item) => ({ ...item, key: item.id }))}
              columns={columns}
              loading={loading}
              pagination={{
                pageSize: 8,
                showSizeChanger: false
              }}
              expandable={{
                expandedRowRender: (record) => (
                  <div style={{ paddingLeft: 24 }}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <div>
                        <Text strong>详情说明：</Text>
                        <Paragraph style={{ whiteSpace: 'pre-line', marginBottom: 0 }}>
                          {record.description?.trim() || '暂无详细描述'}
                        </Paragraph>
                      </div>
                      <Text type="secondary">
                        最近更新：{dayjs(record.updated_at).format('YYYY-MM-DD HH:mm')}
                      </Text>
                    </Space>
                  </div>
                ),
                expandRowByClick: true
              }}
              locale={{
                emptyText: <Empty description="暂时没有记录" />
              }}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title={currentTodo ? `更新反馈：${currentTodo.title}` : '更新反馈'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleAdminUpdate}
        confirmLoading={adminUpdating}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={adminForm} layout="vertical" preserve={false}>
          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="adminReply"
            label="管理员回复"
            rules={[{ max: 5000, message: '回复内容不能超过5000个字符' }]}
          >
            <TextArea
              placeholder="回复用户的处理情况、预计时间等"
              autoSize={{ minRows: 4, maxRows: 6 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PanelTodo;

